import React, { useState, useMemo } from 'react';
import { 
  X, 
  Search, 
  Folder, 
  ChevronRight, 
  Check, 
  Library, 
  Square, 
  CheckSquare,
  FileText
} from 'lucide-react';
import { Program, HomeworkAttachment } from '../types';
import { useTranslation } from '../lib/translations';
import { getFileIcon } from '../lib/fileUtils';

interface ProgramPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  programs: Program[];
  onApply: (attachments: HomeworkAttachment[]) => void;
}

const ProgramPickerModal: React.FC<ProgramPickerModalProps> = ({ 
  isOpen, 
  onClose, 
  programs, 
  onApply 
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    const next = new Set(expandedGroups);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedGroups(next);
  };

  const hierarchy = useMemo(() => {
    const map = new Map<string | null, Program[]>();
    programs.forEach(p => {
      const parent = p.parentId;
      if (!map.has(parent)) map.set(parent, []);
      map.get(parent)!.push(p);
    });
    return map;
  }, [programs]);

  const getRecursiveData = (parentId: string): { groups: string[], files: string[] } => {
    const groups: string[] = [];
    const files: string[] = [];
    
    const p = programs.find(prog => prog.id === parentId);
    if (p) {
      p.attachments.forEach(a => files.push(a.driveId));
    }

    const children = hierarchy.get(parentId) || [];
    children.forEach(child => {
      groups.push(child.id!);
      const sub = getRecursiveData(child.id!);
      groups.push(...sub.groups);
      files.push(...sub.files);
    });
    
    return { groups, files };
  };

  const toggleGroupSelect = (id: string) => {
    const nextGroups = new Set(selectedGroupIds);
    const nextFiles = new Set(selectedFileIds);
    const recursive = getRecursiveData(id);
    
    if (nextGroups.has(id)) {
      nextGroups.delete(id);
      recursive.groups.forEach(g => nextGroups.delete(g));
      recursive.files.forEach(f => nextFiles.delete(f));
      // Also remove current group's files
      const p = programs.find(prog => prog.id === id);
      p?.attachments.forEach(a => nextFiles.delete(a.driveId));
    } else {
      nextGroups.add(id);
      recursive.groups.forEach(g => nextGroups.add(g));
      recursive.files.forEach(f => nextFiles.add(f));
      // Also add current group's files
      const p = programs.find(prog => prog.id === id);
      p?.attachments.forEach(a => nextFiles.add(a.driveId));
    }
    setSelectedGroupIds(nextGroups);
    setSelectedFileIds(nextFiles);
  };

  const toggleFileSelect = (driveId: string) => {
    const next = new Set(selectedFileIds);
    if (next.has(driveId)) next.delete(driveId);
    else next.add(driveId);
    setSelectedFileIds(next);
  };

  const handleApply = () => {
    const appliedAttachments: HomeworkAttachment[] = [];
    const seenDriveIds = new Set<string>();

    programs.forEach(p => {
      p.attachments.forEach(a => {
        if (selectedFileIds.has(a.driveId) && !seenDriveIds.has(a.driveId)) {
          appliedAttachments.push(a);
          seenDriveIds.add(a.driveId);
        }
      });
    });

    onApply(appliedAttachments);
    onClose();
  };

  const renderTree = (parentId: string | null = null, level: number = 0) => {
    const items = hierarchy.get(parentId) || [];
    const searchLower = search.toLowerCase();

    const filtered = items.filter(p => {
      if (!search.trim()) return true;
      const matchesSelf = p.name.toLowerCase().includes(searchLower) || p.description.toLowerCase().includes(searchLower);
      const matchesFiles = p.attachments.some(a => a.name.toLowerCase().includes(searchLower));
      const matchesChildren = (hierarchy.get(p.id!) || []).some(child => child.name.toLowerCase().includes(searchLower));
      return matchesSelf || matchesFiles || matchesChildren;
    });

    return filtered.map(p => {
      const isExpanded = expandedGroups.has(p.id!) || search.trim().length > 0;
      const isSelected = selectedGroupIds.has(p.id!);
      const hasChildren = (hierarchy.get(p.id!) || []).length > 0;
      const hasFiles = p.attachments.length > 0;

      return (
        <div key={p.id} className="space-y-1">
          <div 
            className={`flex items-center gap-3 p-3 rounded-xl transition-all border ${isSelected ? 'bg-purple-50 dark:bg-purple-900/10 border-purple-200 dark:border-purple-500/30 shadow-sm' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-white/5 hover:border-slate-200'} `}
            style={{ marginLeft: `${level * 20}px` }}
          >
            <button 
              onClick={() => toggleGroupSelect(p.id!)}
              className={`p-1 rounded transition-colors ${isSelected ? 'text-purple-600' : 'text-slate-300 hover:text-slate-500'}`}
            >
              {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
            </button>

            {(hasChildren || hasFiles) ? (
              <button 
                onClick={() => toggleExpand(p.id!)}
                className={`p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              >
                <ChevronRight size={14} className="text-slate-400" />
              </button>
            ) : (
              <div className="w-6" />
            )}

            <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-purple-600 text-white' : 'bg-slate-50 dark:bg-slate-800 text-slate-400'}`}>
              <Folder size={14} />
            </div>

            <div className="flex-1 min-w-0" onClick={() => toggleGroupSelect(p.id!)} style={{ cursor: 'pointer' }}>
              <h4 className={`text-[11px] font-black uppercase tracking-widest truncate ${isSelected ? 'text-purple-700 dark:text-purple-300' : 'text-slate-700 dark:text-slate-200'}`}>
                {p.name}
              </h4>
              {p.attachments.length > 0 && (
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                  {p.attachments.length} assets
                </p>
              )}
            </div>
          </div>

          {isExpanded && (
            <div className="space-y-1">
              {/* Render Files in this Group */}
              {p.attachments
                .filter(a => !search.trim() || a.name.toLowerCase().includes(searchLower))
                .map(file => {
                  const isFileSelected = selectedFileIds.has(file.driveId);
                  return (
                    <div 
                      key={file.driveId}
                      className={`flex items-center gap-3 p-2 rounded-lg transition-all border ml-5 ${isFileSelected ? 'bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-500/20' : 'bg-transparent border-transparent opacity-70 hover:opacity-100'}`}
                      style={{ marginLeft: `${(level + 1) * 20}px` }}
                    >
                      <button 
                        onClick={() => toggleFileSelect(file.driveId)}
                        className={`p-1 rounded transition-colors ${isFileSelected ? 'text-indigo-600' : 'text-slate-300 hover:text-slate-500'}`}
                      >
                        {isFileSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                      </button>
                      <div className="text-slate-400">
                        {getFileIcon(file.mimeType, 12)}
                      </div>
                      <span className={`text-[10px] font-bold truncate flex-1 ${isFileSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-500 dark:text-slate-400'}`}>
                        {file.name}
                      </span>
                    </div>
                  );
                })}
              {/* Render Subgroups */}
              {renderTree(p.id!, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300 !mt-0">
      <div className="bg-white dark:bg-slate-900 w-full max-w-xl h-[80vh] rounded-2xl border border-slate-200 dark:border-white/5 shadow-2xl flex flex-col overflow-hidden animate-in zoom-in duration-300">
        
        <div className="p-6 border-b border-slate-100 dark:border-white/5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 rounded-xl flex items-center justify-center">
              <Library size={20} />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">{t.programs.bulkAdd}</h2>
              <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">{t.programs.selectGroup}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all active:scale-90">
            <X size={24} />
          </button>
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-950/50 border-b border-slate-100 dark:border-white/5 shrink-0">
          <div className="relative group">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" size={16} />
            <input 
              type="text" 
              placeholder="Search curriculum..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl py-2.5 pl-10 pr-4 text-xs font-medium focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none transition-all shadow-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-2 custom-scrollbar">
          {programs.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
              <Library size={40} className="mb-4" />
              <p className="text-[10px] font-black uppercase tracking-widest">{t.programs.noGroups}</p>
            </div>
          ) : (
            renderTree(null)
          )}
        </div>

        <div className="p-6 border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-950 shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t.programs.selection}</span>
              <span className="text-xs font-black text-purple-600">{t.programs.assetsSelected.replace('{count}', selectedFileIds.size.toString())}</span>
            </div>
            <button 
              onClick={handleApply}
              disabled={selectedFileIds.size === 0}
              className="px-8 py-3 bg-purple-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-purple-500/20 hover:bg-purple-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
            >
              <Check size={16} />
              {t.common.confirm}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramPickerModal;